"use client";

import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import Navbar from "./components/layout/Navbar";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import ProfileDetailPage from "./pages/ProfileDetailPage";
import SearchPage from "./pages/SearchPage";
import WishlistPage from "./pages/WishlistPage";
import Membership from "./pages/Membership";
import SettingsPage from "./pages/SettingsPage";
import MultiStepEditModal from "./pages/MultiStepEditModal";
import { ToastProvider } from "./contexts/ToastContext"; // ✅ named import
import TermsPage from "./pages/Termspage";
import PrivacyPage from "./pages/Privacypage";
import RequestedEnquiries from "./pages/RequestedEnquiries";

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation(); // ✅ correct way
  const isUnverified = user && Number(user.isVerified) === 0;
  const onSettingsPage = location.pathname === "/settings"; // ✅ works dynamically
  const onEditProfilePage = location.pathname === "/edit-profile";

  // Global scroll locking for unverified users
  useEffect(() => {
    if (isUnverified && !onSettingsPage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isUnverified, onSettingsPage]);

  return (
    <Box>
      <Navbar />
      <Box sx={{ pt: { xs: "90px", md: "110px" }, minHeight: "100vh" }}>
        <Routes>
          <Route
            path="/home"
            element={user ? <HomePage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/"
            element={!user ? <AuthPage /> : <Navigate to="/home" replace />}
          />
          <Route
            path="/profile/:user_id"
            element={user ? <ProfileDetailPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/search"
            element={user ? <SearchPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/wishlist"
            element={user ? <WishlistPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/membership"
            element={user ? <Membership /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings"
            element={user ? <SettingsPage /> : <Navigate to="/" replace />}
          />
          <Route path="/edit-profile" element={<MultiStepEditModal />} />
          <Route
            path="/requested-enquiries"
            element={
              user ? <RequestedEnquiries /> : <Navigate to="/" replace />
            }
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>

        {/* Global Blur Overlay for Unverified Users */}
        {isUnverified && !onSettingsPage && !onEditProfilePage && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(255,255,255,0.6)",
              zIndex: (theme) => theme.zIndex.drawer, // Below Navbar (drawer + 1)
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{ p: 4, borderRadius: 1, textAlign: "center", maxWidth: 400 }}
            >
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "text.primary",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                }}
              >
                Your account is not verified yet.{" "}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 2, textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}
              >
                Your account is currently under review.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <WishlistProvider>
          <AppRoutes />
        </WishlistProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
